import { api } from "../../lib/api.ts";
import type { AnalyticsEventName } from "./catalog.ts";

/**
 * `POST /api/v1/analytics/events` — batched analytics ingestion
 * (`analytics` endpoint group, docs/_spec-backbone.md §6/§18,
 * FR-ANALYTICS-002/006/007). Batch size capped at 20 server-side; anonymous
 * (pre-auth) batches only accept the LOCKED pre-auth event set.
 *
 * NOTE: this endpoint is not yet present in the generated
 * `@jod/api-client` schema (`packages/api-client/src/generated/schema.ts`)
 * in this worktree, so the request is typed locally against the documented
 * wire contract rather than through the typed `paths` union — mirrors the
 * `MinimalCustomerApiClient` escape hatch already used by
 * `features/api/backend.ts` for envelope-shaped responses the generator
 * doesn't model. Once the backend lands in this branch and
 * `pnpm api-client:generate` regenerates the schema, this can switch to the
 * fully-typed client path; the wire shape here matches the documented
 * contract exactly (camelCase `eventId`/`eventName`/`occurredAt`/...).
 */
export const ANALYTICS_MAX_BATCH_SIZE = 20;

export interface AnalyticsEventWire {
  eventId?: string;
  eventName: AnalyticsEventName;
  occurredAt?: string;
  properties?: Record<string, unknown>;
  venueId?: string;
  branchId?: string;
  venueSessionId?: string;
  /** Per-launch client-generated token — required for the anonymous (pre-auth) path. */
  clientId?: string;
}

export interface IngestAnalyticsEventsResult {
  accepted: number;
  duplicates: number;
  rejected: { index: number; eventName: string; reason: string }[];
}

interface AnalyticsApiCallResult {
  data?: unknown;
  error?: unknown;
}

type MinimalAnalyticsClient = {
  POST: (
    path: string,
    options: { body: unknown; keepalive?: boolean },
  ) => Promise<AnalyticsApiCallResult>;
};

export interface PostAnalyticsBatchOptions {
  /** Use `fetch(..., { keepalive: true })` semantics for page-hide flushes. */
  keepalive?: boolean;
  client?: MinimalAnalyticsClient;
}

/**
 * Posts one batch. Throws on transport/validation failure — callers
 * (`analytics-client.ts`) are expected to swallow the error: analytics must
 * never break UX.
 */
export async function postAnalyticsBatch(
  events: AnalyticsEventWire[],
  options: PostAnalyticsBatchOptions = {},
): Promise<IngestAnalyticsEventsResult> {
  if (events.length === 0) {
    return { accepted: 0, duplicates: 0, rejected: [] };
  }
  const client = options.client ?? (api as unknown as MinimalAnalyticsClient);
  const result = await client.POST("/api/v1/analytics/events", {
    body: { events },
    keepalive: options.keepalive,
  });

  if (result.error) {
    throw new Error("analytics ingest failed");
  }
  const envelope = result.data as
    | { success?: boolean; data?: IngestAnalyticsEventsResult }
    | undefined;
  if (!envelope || envelope.success === false || envelope.data == null) {
    throw new Error("analytics ingest failed");
  }
  return envelope.data;
}
